import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { isAdmin } from "@/lib/permissions";
import { healthPresentation, resolveConnectionState } from "@/lib/connector-status-ux";
import { getAllMarketplaces, getMarketplaceName } from "@/lib/providers/ebay-adapter";
import { parseListingDryRunResult, type ListingDryRunResult } from "@/lib/providers/listing-contract";

type EbayModeBadge = "NOT_CONFIGURED" | "DRY_RUN" | "LIVE";

interface EbayDashboard {
  listing_counts?: {
    published?: number;
    ready?: number;
    error?: number;
  };
  dirty_count?: number;
  dead_letter_count?: number;
  unknown_sku_count?: number;
  connection_blocked_count?: number;
}

function HonestModeBadge({ mode }: { mode: EbayModeBadge }) {
  const styles: Record<EbayModeBadge, string> = {
    NOT_CONFIGURED: "bg-red-50 text-red-700 border border-red-200",
    DRY_RUN: "bg-amber-50 text-amber-700 border border-amber-200",
    LIVE: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-bold ${styles[mode]}`}>
      {mode === "NOT_CONFIGURED" ? "NOT CONFIGURED" : mode === "DRY_RUN" ? "DRY RUN" : "LIVE"}
    </span>
  );
}

type StatusTone = "positive" | "neutral" | "warning" | "danger" | "info";

/** Presentation only — tone follows the derived state, never auth alone. */
function statusToneClasses(tone: StatusTone): string {
  if (tone === "positive") return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  if (tone === "danger") return "bg-red-50 text-red-700 border border-red-200";
  if (tone === "warning") return "bg-amber-50 text-amber-700 border border-amber-200";
  return "bg-slate-100 text-slate-600 border border-slate-200";
}

function statusDotClasses(tone: StatusTone): string {
  if (tone === "positive") return "bg-emerald-500";
  if (tone === "danger") return "bg-red-500";
  if (tone === "warning") return "bg-amber-400";
  return "bg-slate-300";
}

export default function EbayConnectorConfig() {
  const { connectorId } = useParams<{ connectorId: string }>();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [actionError, setActionError] = useState("");
  const [oauthInitiating, setOauthInitiating] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<ListingDryRunResult | null>(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [prereqSyncBusy, setPrereqSyncBusy] = useState<"policies" | "locations" | null>(null);
  const [actionNotice, setActionNotice] = useState("");
  const [createLocationOpen, setCreateLocationOpen] = useState(false);
  const [createLocationBusy, setCreateLocationBusy] = useState(false);

  const isAdminUser = isAdmin(role);

  const { data: connector, isLoading: connLoading } = useQuery({
    queryKey: ["connector", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("connectors")
        .select("id, name, provider, status, enabled, connection_status, auth_status, health_status, external_account_id, marketplace_site, capabilities")
        .eq("id", connectorId!)
        .eq("org_id", org!.id)
        .maybeSingle();
      return data;
    },
  });

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["ebay-config", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("connector_ebay_config")
        .select("*")
        .eq("connector_id", connectorId!)
        .maybeSingle();
      return data;
    },
  });

  const { data: policies } = useQuery({
    queryKey: ["ebay-policies", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_business_policies")
        .select("*")
        .eq("connector_id", connectorId!)
        .eq("org_id", org!.id)
        .eq("is_active", true)
        .order("policy_type");
      return data ?? [];
    },
  });

  const { data: locations } = useQuery({
    queryKey: ["ebay-locations", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_inventory_locations")
        .select("*, locations(id, name)")
        .eq("connector_id", connectorId!)
        .eq("org_id", org!.id)
        .eq("is_active", true);
      return data ?? [];
    },
  });

  const { data: health } = useQuery({
    queryKey: ["ebay-health", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_connection_health")
        .select("*")
        .eq("connector_id", connectorId!)
        .maybeSingle();
      return data;
    },
  });

  const { data: dashboard } = useQuery({
    queryKey: ["ebay-dashboard", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase.rpc("get_enhanced_ebay_store_dashboard", {
        p_org_id: org!.id,
        p_connector_id: connectorId!,
      });
      return data as EbayDashboard | null;
    },
  });

  const { data: deadLetters } = useQuery({
    queryKey: ["ebay-dead-letters", org?.id, connectorId],
    enabled: !!org?.id && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_provider_operations")
        .select("id, operation_type, listing_id, error_code, error_message, attempt_count, dead_lettered_at, dead_letter_reason")
        .eq("org_id", org!.id)
        .eq("connector_id", connectorId!)
        .not("dead_lettered_at", "is", null)
        .order("dead_lettered_at", { ascending: false })
        .limit(20);
      return data ?? [];
    },
  });

  const initConfig = useMutation({
    mutationFn: async (params: { marketplace_id: string; currency: string; environment: string }) => {
      const { data, error } = await supabase.rpc("initialize_ebay_connector_config", {
        p_org_id: org!.id,
        p_connector_id: connectorId!,
        p_marketplace_id: params.marketplace_id,
        p_currency: params.currency,
      });
      if (error) throw error;
      // Set environment
      await supabase
        .from("connector_ebay_config")
        .update({ environment: params.environment })
        .eq("connector_id", connectorId!)
        .eq("org_id", org!.id);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ebay-config", org?.id, connectorId] });
      setEditOpen(false);
    },
  });

  const syncPrerequisites = async (action: "policies" | "locations") => {
    if (!connectorId || !org?.id) return;
    setPrereqSyncBusy(action);
    setActionError("");
    setActionNotice("");
    try {
      const res = await supabase.functions.invoke("ebay-prerequisites-sync", {
        body: { connector_id: connectorId, org_id: org.id, action },
      });
      const payload = res.data as { success?: boolean; error?: string; policy_count?: number; location_count?: number } | null;
      if (res.error || !payload?.success) {
        throw new Error(payload?.error || res.error?.message || "eBay prerequisite sync failed");
      }
      if (action === "policies") {
        setActionNotice(`Synced ${payload.policy_count ?? 0} eBay business policies.`);
        await queryClient.invalidateQueries({ queryKey: ["ebay-policies", org.id, connectorId] });
      } else {
        setActionNotice(`Synced ${payload.location_count ?? 0} eBay inventory locations.`);
        await queryClient.invalidateQueries({ queryKey: ["ebay-locations", org.id, connectorId] });
      }
      await queryClient.invalidateQueries({ queryKey: ["ebay-config", org.id, connectorId] });
    } catch (err) {
      setActionError((err as Error).message);
    }
    setPrereqSyncBusy(null);
  };

  const createInventoryLocation = async () => {
    if (!connectorId || !org?.id) return;
    const form = document.getElementById("ebay-location-create-form") as HTMLFormElement | null;
    if (!form) return;
    const fd = new FormData(form);
    setCreateLocationBusy(true);
    setActionError("");
    setActionNotice("");
    try {
      const res = await supabase.functions.invoke("ebay-prerequisites-sync", {
        body: {
          connector_id: connectorId,
          org_id: org.id,
          action: "create_location",
          location_name: String(fd.get("location_name") || ""),
          city: String(fd.get("city") || ""),
          state: String(fd.get("state") || ""),
          postal_code: String(fd.get("postal_code") || ""),
          country: "AU",
        },
      });
      const payload = res.data as { success?: boolean; error?: string; location_count?: number } | null;
      if (res.error || !payload?.success) {
        throw new Error(payload?.error || res.error?.message || "Failed to create eBay inventory location");
      }
      setActionNotice("Created and synced the eBay inventory location.");
      setCreateLocationOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["ebay-locations", org.id, connectorId] });
      await queryClient.invalidateQueries({ queryKey: ["ebay-config", org.id, connectorId] });
    } catch (err) {
      setActionError((err as Error).message);
    }
    setCreateLocationBusy(false);
  };

  const isConnected = connector?.auth_status === "connected" || connector?.auth_status === "authorized";
  const needsReauthorization = connector?.auth_status === "expired" || connector?.auth_status === "revoked";
  const marketplace = config?.marketplace_id || connector?.marketplace_site || "EBAY_AU";
  // Persisted trigger-derived connection_status is the connection source.
  const connection = resolveConnectionState({
    connection_status: connector?.connection_status,
    auth_status: connector?.auth_status,
    health_status: connector?.health_status,
  });
  // Health requires affirmative evidence — the eBay health probe first, then
  // the connector health column. Unknown is reported as unknown.
  const healthEvidence = healthPresentation({
    auth_status: connector?.auth_status,
    health_status: health?.overall_health ?? connector?.health_status,
  });
  const setupIndicators: Array<{ label: string; tone: StatusTone; value: string }> = [
    { label: "Connection Health", tone: healthEvidence.tone, value: healthEvidence.label },
    {
      label: "Seller Authorization",
      tone: isConnected ? "positive" : needsReauthorization ? "warning" : "neutral",
      value: connector?.auth_status || "not connected",
    },
    {
      label: "Token Health",
      tone: health?.overall_health ? healthEvidence.tone : "neutral",
      value: health?.overall_health || "Not reported",
    },
    {
      label: "Business Policies",
      tone: (policies?.length || 0) > 0 ? "positive" : "neutral",
      value: `${policies?.length || 0} configured`,
    },
  ];

  const handleOAuthInitiate = async () => {
    if (!connectorId || !org?.id) return;
    setOauthInitiating(true);
    setActionError("");

    try {
      const res = await supabase.functions.invoke("ebay-oauth-initiate", {
        body: { connector_id: connectorId, org_id: org.id },
      });
      const payload = res.data as { success?: boolean; authorization_url?: string; error?: string } | null;
      if (res.error || !payload?.success || !payload.authorization_url) {
        throw new Error(payload?.error || res.error?.message || "Failed to start eBay authorization");
      }
      window.location.assign(payload.authorization_url);
    } catch (err) {
      setActionError((err as Error).message);
    }
    setOauthInitiating(false);
  };

  const handleDryRunTest = async () => {
    if (!connectorId || !org?.id) return;
    setDryRunBusy(true);
    setDryRunResult(null);

    try {
      // Get a listing to test
      const { data: listing } = await supabase
        .from("channel_listings")
        .select("id, channel_title, intended_version")
        .eq("org_id", org.id)
        .eq("connector_id", connectorId)
        .not("status", "eq", "archived")
        .limit(1)
        .maybeSingle();

      if (!listing) {
        setDryRunResult(parseListingDryRunResult({ success: false, error: "No listing found for this connector. Create a listing first." }));
        setDryRunBusy(false);
        return;
      }

      const res = await supabase.functions.invoke("ebay-inventory-sync", {
        body: { listing_id: listing.id, org_id: org.id, mode: "dry_run", sync_type: "full" },
      });

      setDryRunResult(parseListingDryRunResult(res.data ?? { success: false, error: res.error?.message }));
    } catch (err) {
      setDryRunResult(parseListingDryRunResult({ success: false, error: (err as Error).message }));
    }
    setDryRunBusy(false);
  };

  const handleManualRetry = async (operationId: string) => {
    if (!org?.id || !connectorId) return;
    await supabase.rpc("bulk_retry_operations", {
      p_org_id: org.id,
      p_connector_id: connectorId,
      p_operation_ids: [operationId],
    });
    queryClient.invalidateQueries({ queryKey: ["ebay-dead-letters", org.id, connectorId] });
  };

  if (connLoading || configLoading) return <LoadingState />;

  return (
    <div>
      <PageHeader
        title={connector?.name || "eBay Connector"}
        subtitle={`${getMarketplaceName(marketplace)} · ${connector?.provider || "ebay"}`}
        backTo="/integrations"
      />

      {actionError && (
        <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{actionError}</div>
      )}
      {actionNotice && (
        <div className="mb-4 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{actionNotice}</div>
      )}

      {/* Connection Status Banner */}
      <div className="mb-6 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-xs px-2 py-1 rounded-full font-bold ${statusToneClasses(connection.tone)}`}>
            {connection.label}
          </span>
          {isConnected && (
            <span className="text-sm text-emerald-600">
              eBay seller account authorised. Provider health and availability are reported separately.
            </span>
          )}
          {needsReauthorization && (
            <span className="text-sm text-amber-600">
              eBay authorization expired or was revoked. Reconnect the eBay account to continue.
            </span>
          )}
          {!isConnected && !needsReauthorization && (
            <span className="text-sm text-slate-500">
              Connect an eBay seller account to enable live publication, order import, and fulfilment.
            </span>
          )}
        </div>

        {/* Setup Status Indicators */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {setupIndicators.map((item) => (
            <div key={item.label} className="border border-slate-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${statusDotClasses(item.tone)}`} />
                <span className="text-xs font-medium text-slate-600">{item.label}</span>
              </div>
              <p className="text-xs text-slate-500">{item.value}</p>
            </div>
          ))}
        </div>

        {isAdminUser && (
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <button
              onClick={handleOAuthInitiate}
              disabled={oauthInitiating}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {oauthInitiating ? "Opening..." : needsReauthorization ? "Reconnect eBay Account" : isConnected ? "Re-authorize" : "Connect eBay Account"}
            </button>
            <button
              onClick={() => setEditOpen(true)}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Edit Configuration
            </button>
            <button
              onClick={handleDryRunTest}
              disabled={dryRunBusy}
              className="px-4 py-2 bg-amber-50 border border-amber-200 hover:bg-amber-100 text-amber-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {dryRunBusy ? "Validating..." : "Validate / Dry Run"}
            </button>
          </div>
        )}
      </div>

      {/* Dry Run Result */}
      {dryRunResult && (
        <div className={`mb-6 rounded-xl border p-4 ${dryRunResult.mode === "DRY_RUN" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}>
          <div className="flex items-center gap-2 mb-2">
            <HonestModeBadge mode={dryRunResult.mode} />
            <span className="text-sm font-medium text-slate-700">
              {dryRunResult.message || dryRunResult.error || "Result"}
            </span>
          </div>
          {(dryRunResult.plan?.steps.length ?? 0) > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-slate-500 mb-2">Publication Plan Preview:</p>
              <div className="space-y-1">
                {dryRunResult.plan!.steps.map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-4 h-4 flex items-center justify-center bg-slate-200 rounded-full text-slate-500 text-xs font-medium">{step.step}</span>
                    <span className={step.skipped ? "text-slate-300 line-through" : ""}>{step.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(dryRunResult.plan?.validation_issues.length ?? 0) > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-red-600 mb-1">Validation Issues:</p>
              {dryRunResult.plan!.validation_issues.map((issue, i) => (
                <p key={i} className="text-xs text-red-600">{issue.code}: {issue.message}</p>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Setup */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Account Configuration</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {[
                { label: "Environment", value: config?.environment || "not set" },
                { label: "Marketplace", value: getMarketplaceName(marketplace) },
                { label: "Currency", value: config?.currency || "—" },
                { label: "Seller Username", value: config?.seller_username || "—" },
                { label: "Taxonomy Version", value: config?.category_taxonomy_version || "Not synced" },
                { label: "Last Policy Sync", value: config?.last_policy_sync_at ? new Date(config.last_policy_sync_at).toLocaleDateString() : "Never" },
                { label: "Last Location Sync", value: config?.last_location_sync_at ? new Date(config.last_location_sync_at).toLocaleDateString() : "Never" },
                { label: "Rate Limit Window", value: config?.rate_limit_requests_in_window ? `${config.rate_limit_requests_in_window} req` : "—" },
              ].map((item) => (
                <div key={item.label} className="border border-slate-100 rounded-lg p-3">
                  <p className="text-xs text-slate-400">{item.label}</p>
                  <p className="text-slate-700 font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Business Policies */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Business Policies</h3>
                <span className="text-xs text-slate-400">Scoped per connector — one seller account cannot use another account's policies</span>
              </div>
              {isAdminUser && (
                <button
                  onClick={() => syncPrerequisites("policies")}
                  disabled={!isConnected || prereqSyncBusy !== null}
                  className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                >
                  {prereqSyncBusy === "policies" ? "Syncing..." : "Sync policies from eBay"}
                </button>
              )}
            </div>
            {policies && policies.length > 0 ? (
              <div className="space-y-2">
                {policies.map((p: Record<string, unknown>) => (
                  <div key={`${p.policy_type}-${p.external_policy_id}`} className="flex items-center justify-between p-2 border border-slate-100 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-700">{p.policy_name as string || p.external_policy_id as string}</p>
                      <p className="text-xs text-slate-400">{p.policy_type as string} · ID: {p.external_policy_id as string}</p>
                    </div>
                    {p.is_default && (
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Default</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">
                {isConnected ? "No policies synced yet. Sync policies from the eBay account." : "Connect the eBay account to discover policies."}
              </p>
            )}
          </div>

          {/* Inventory Locations */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Inventory Locations</h3>
                <span className="text-xs text-slate-400">Missing required location blocks LIVE publish</span>
              </div>
              {isAdminUser && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setCreateLocationOpen(true)}
                    disabled={!isConnected || prereqSyncBusy !== null || createLocationBusy}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                  >
                    Create eBay inventory location
                  </button>
                  <button
                    onClick={() => syncPrerequisites("locations")}
                    disabled={!isConnected || prereqSyncBusy !== null || createLocationBusy}
                    className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                  >
                    {prereqSyncBusy === "locations" ? "Syncing..." : "Sync locations from eBay"}
                  </button>
                </div>
              )}
            </div>
            {locations && locations.length > 0 ? (
              <div className="space-y-2">
                {locations.map((loc: Record<string, unknown>) => (
                  <div key={loc.merchant_location_key as string} className="flex items-center justify-between p-2 border border-slate-100 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-700">{loc.location_name as string || loc.merchant_location_key as string}</p>
                      <p className="text-xs text-slate-400">
                        Key: {loc.merchant_location_key as string}
                        {(loc.locations as Record<string, unknown> | null)?.name ? ` · Tradivo: ${(loc.locations as Record<string, unknown>).name}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {loc.provider_confirmed_at ? (
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Provider confirmed</span>
                      ) : (
                        <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">Intended only</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No inventory locations mapped.</p>
            )}
          </div>

          {/* Dead Letters */}
          {deadLetters && deadLetters.length > 0 && (
            <div className="bg-white rounded-xl border border-red-200 p-5">
              <h3 className="text-sm font-semibold text-red-700 mb-3">Dead Letters ({deadLetters.length})</h3>
              <div className="space-y-2">
                {deadLetters.map((op: Record<string, unknown>) => (
                  <div key={op.id as string} className="border border-red-100 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-red-700">{op.operation_type as string} — {op.error_code as string || "UNKNOWN"}</p>
                        <p className="text-xs text-red-600">{op.error_message as string || op.dead_letter_reason as string}</p>
                        <p className="text-xs text-slate-400">{op.attempt_count as number} attempts · {op.dead_lettered_at ? new Date(op.dead_lettered_at as string).toLocaleString() : ""}</p>
                      </div>
                      <button
                        onClick={() => handleManualRetry(op.id as string)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap ml-2"
                      >
                        Manual Retry
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Connection Health */}
          {health && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Connection Health</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { label: "Last API success", value: health.last_successful_api_call_at ? new Date(health.last_successful_api_call_at).toLocaleString() : "—" },
                  { label: "Last API failure", value: health.last_failed_api_call_at ? new Date(health.last_failed_api_call_at).toLocaleString() : "—" },
                  { label: "Token refresh", value: health.last_token_refresh_at ? new Date(health.last_token_refresh_at).toLocaleString() : "—" },
                  { label: "Refresh failures", value: String(health.token_refresh_failures || 0) },
                  { label: "Rate limits (24h)", value: String(health.rate_limit_count_24h || 0) },
                  { label: "Last order import", value: health.last_order_import_at ? new Date(health.last_order_import_at).toLocaleString() : "—" },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs text-slate-400">{item.label}</p>
                    <p className="text-slate-700">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column — Dashboard */}
        <div className="space-y-6">
          {/* Operational Dashboard */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Store Dashboard</h3>
            <div className="space-y-3">
              {[
                { label: "Published", value: dashboard?.listing_counts?.published ?? 0, color: "emerald" },
                { label: "Ready", value: dashboard?.listing_counts?.ready ?? 0, color: "slate" },
                { label: "Dirty", value: dashboard?.dirty_count ?? 0, color: "sky" },
                { label: "Error", value: dashboard?.listing_counts?.error ?? 0, color: "red" },
                { label: "Dead Letters", value: deadLetters?.length ?? 0, color: "red" },
                { label: "Dead letter ops", value: dashboard?.dead_letter_count ?? 0, color: "red" },
                { label: "Unknown SKU orders", value: dashboard?.unknown_sku_count ?? 0, color: "amber" },
                { label: "Connection blocked", value: dashboard?.connection_blocked_count ?? 0, color: "red" },
              ].map(({ label, value, color }) => (
                <div key={label} className={`flex items-center justify-between px-3 py-2 rounded-lg bg-${color}-50`}>
                  <span className={`text-sm text-${color}-700`}>{label}</span>
                  <span className={`text-sm font-semibold text-${color}-700`}>{value as number}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Marketplace Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Marketplace</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Environment</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${config?.environment === "production" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {config?.environment || "not set"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Marketplace ID</span>
                <span className="text-slate-800 font-mono text-xs">{marketplace}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Currency</span>
                <span className="text-slate-800">{config?.currency || "—"}</span>
              </div>
            </div>
          </div>

          {/* Webhook Status */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Webhook Status</h3>
            <p className="text-xs text-slate-400 mb-3">
              Webhooks deliver event notifications from eBay. Store isolation ensures Store A events are never applied to Store B.
            </p>
            <div className="text-sm">
              <span className="text-slate-500">Architecture: </span>
              <span className="text-slate-700">signature verification, deduplication, domain event queuing</span>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={createLocationOpen}
        onClose={() => setCreateLocationOpen(false)}
        title="Create eBay Inventory Location"
        footer={
          <>
            <button
              onClick={() => setCreateLocationOpen(false)}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={createInventoryLocation}
              disabled={createLocationBusy}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg"
            >
              {createLocationBusy ? "Creating..." : "Create on eBay"}
            </button>
          </>
        }
      >
        <form id="ebay-location-create-form" className="space-y-4">
          <p className="text-sm text-slate-500">
            This creates a real merchant inventory location in the connected eBay seller account and then maps it back to Tradivo.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Location name</label>
            <input name="location_name" required placeholder="Universal Gear Warehouse" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Suburb / city</label>
            <input name="city" required className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">State</label>
              <select name="state" defaultValue="NSW" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Postcode</label>
              <input name="postal_code" required inputMode="numeric" maxLength={4} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Country</label>
            <input value="Australia (AU)" readOnly className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-500" />
          </div>
        </form>
      </Modal>

      {/* Edit Config Modal */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit eBay Connector Configuration"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={() => {
                const fd = new FormData(document.getElementById("ebay-config-form") as HTMLFormElement);
                initConfig.mutate({
                  marketplace_id: fd.get("marketplace_id") as string,
                  currency: fd.get("currency") as string,
                  environment: fd.get("environment") as string,
                });
              }}
              disabled={initConfig.isPending}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {initConfig.isPending ? "Saving..." : "Save"}
            </button>
          </>
        }
      >
        <form id="ebay-config-form" className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Environment</label>
            <select name="environment" defaultValue={config?.environment || "sandbox"} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="sandbox">Sandbox (testing)</option>
              <option value="production">Production (live)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Marketplace</label>
            <select name="marketplace_id" defaultValue={marketplace} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              {getAllMarketplaces().map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({m.currency})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Currency</label>
            <select name="currency" defaultValue={config?.currency || "AUD"} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              {["AUD", "USD", "GBP", "EUR", "CAD"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </form>
      </Modal>

    </div>
  );
}
